import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Vietnamese mirror of the NAATI vs court-registered translator guide. */
const TITLE = "Dịch thuật NAATI và dịch viên đăng ký toà án Thái Lan — chọn loại nào?";
const DESC =
  "Khi nào bắt buộc dùng bản dịch NAATI, khi nào nên dùng dịch viên đăng ký toà án hoặc Liên đoàn Luật sư Thái Lan, và cách tránh phải làm lại hai lần.";
const PATH = "/blog/naati-vs-court-registered-translator";
const URL = `/vi${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "NAATI là gì?",
    answer:
      "NAATI là cơ quan công nhận biên dịch và phiên dịch của Úc. Nhiều cơ quan Úc chỉ tiếp nhận bản dịch do người được NAATI công nhận thực hiện, kèm dấu và số công nhận.",
  },
  {
    question: "Dịch viên đăng ký toà án Thái Lan dùng cho việc gì?",
    answer:
      "Dùng cho hồ sơ nộp trong Thái Lan hoặc cho tuyến chứng nhận qua Cục Lãnh sự và đại sứ quán, khi cơ quan tiếp nhận yêu cầu bản dịch của dịch viên được toà án hoặc Liên đoàn Luật sư công nhận.",
  },
  {
    question: "Bản dịch NAATI có được Cục Lãnh sự Thái Lan chứng nhận không?",
    answer:
      "Không đương nhiên. Tuyến NAATI và tuyến chứng nhận của Thái Lan là hai hệ khác nhau; hãy xác nhận với cơ quan tiếp nhận xem họ cần loại nào trước khi đặt dịch.",
  },
  {
    question: "Làm sao để không phải trả tiền hai lần?",
    answer:
      "Hỏi rõ cơ quan tiếp nhận: cần loại chứng nhận nào, ngôn ngữ nào, và có yêu cầu bản gốc kèm theo không. Sau đó mới chọn tuyến dịch phù hợp.",
  },
  {
    question: "Bản dịch có thời hạn không?",
    answer:
      "Bản dịch thường không có thời hạn riêng, nhưng giấy tờ nguồn thì có. Nếu giấy tờ nguồn quá hạn, bản dịch cũng mất giá trị sử dụng.",
  },
  {
    question: "Có cần dịch cả con dấu không?",
    answer:
      "Có. Con dấu, tem, chữ ký và mọi dòng chú thích đều phải được thể hiện trong bản dịch, nếu không hồ sơ có thể bị trả lại.",
  },
  {
    question: "Hồ sơ di trú Úc thì chọn tuyến nào?",
    answer:
      "Thông thường là bản dịch NAATI; một số giấy tờ vẫn cần thêm chứng nhận của Cục Lãnh sự tuỳ yêu cầu của cơ quan xử lý.",
  },
];

export const Route = createFileRoute("/vi/blog/naati-vs-court-registered-translator")({
  component: ViNaatiVsCourt,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "vi" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("naati-vs-court-registered-translator", "vi") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "dịch thuật NAATI, dịch viên đăng ký toà án Thái Lan, dịch công chứng Thái Lan, dịch thuật hồ sơ di trú Úc",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "vi"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Trang chủ", url: "/vi" },
              { name: "Hướng dẫn", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("naati-vs-court-registered-translator"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Translation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DECIDE: Array<{ case_: string; route: string }> = [
  { case_: "Nộp cho cơ quan di trú, đại học hoặc hội đồng nghề nghiệp tại Úc", route: "Bản dịch NAATI" },
  { case_: "Nộp cho toà án hoặc cơ quan hành chính tại Thái Lan", route: "Dịch viên được toà án hoặc Liên đoàn Luật sư công nhận" },
  { case_: "Đi tuyến Cục Lãnh sự rồi đại sứ quán nước thứ ba", route: "Dịch viên mà Cục Lãnh sự chấp nhận" },
  { case_: "Dùng cho đối tác thương mại, không nộp cơ quan nhà nước", route: "Bản dịch có xác nhận của công ty dịch thuật" },
];

const STEPS: string[] = [
  "Xác nhận với cơ quan tiếp nhận: loại chứng nhận, ngôn ngữ và có cần bản gốc kèm theo",
  "Kiểm tra thời hạn của giấy tờ nguồn và đếm ngược từ hạn nộp",
  "Chọn tuyến dịch phù hợp trước khi đặt dịch, tránh làm lại",
  "Dịch toàn văn kèm con dấu, tem và chú thích",
  "Kiểm tra cách viết tên trùng khớp với hộ chiếu và các giấy tờ khác",
  "Hoàn tất các lớp chứng nhận còn lại theo đúng thứ tự",
];

const MISTAKES: string[] = [
  "Đặt dịch trước khi biết cơ quan tiếp nhận yêu cầu tuyến nào",
  "Dùng bản dịch NAATI cho hồ sơ chỉ chấp nhận dịch viên đăng ký tại Thái Lan (và ngược lại)",
  "Không dịch con dấu và chú thích",
  "Tên viết khác nhau giữa bản dịch và hộ chiếu",
  "Giấy tờ nguồn quá hạn khi nộp",
];

function ViNaatiVsCourt() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/vi" className="text-muted-foreground hover:text-foreground">
          Trang chủ
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Hướng dẫn
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="naati-vs-court-registered-translator" locale="vi" />
      <p className="answer-summary speakable-summary">
        Chọn tuyến dịch theo cơ quan tiếp nhận, không theo giá. Cơ quan Úc thường yêu cầu bản dịch NAATI; hồ sơ nộp
        trong Thái Lan hoặc đi tuyến Cục Lãnh sự thường cần dịch viên được toà án hoặc Liên đoàn Luật sư công nhận. Hỏi
        rõ yêu cầu trước khi đặt dịch là cách duy nhất để không phải làm lại.
      </p>

      <h2>1. Chọn tuyến theo tình huống</h2>
      <ul>
        {DECIDE.map((d) => (
          <li key={d.case_}>
            <strong>{d.case_}:</strong> {d.route}
          </li>
        ))}
      </ul>

      <h2>2. Thứ tự thực hiện</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Lỗi thường gặp</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Câu hỏi thường gặp</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Nguồn tham khảo và dịch vụ liên quan</h2>
      <p className="text-sm">
        Tham khảo:{" "}
        <a href="https://www.naati.com.au/" target="_blank" rel="noreferrer">
          NAATI (Úc)
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Cục Lãnh sự, Bộ Ngoại giao Thái Lan
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/vi/naati" className="text-primary underline">
            Dịch thuật có chứng nhận NAATI
          </Link>
        </li>
        <li>
          <Link to="/vi/sworn" className="text-primary underline">
            Dịch viên đăng ký toà án và Liên đoàn Luật sư
          </Link>
        </li>
        <li>
          <Link to="/vi/lgz" className="text-primary underline">
            Chứng nhận và hợp pháp hoá giấy tờ
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Nội dung rà soát ngày {PUBLISHED}. Đây là thông tin chung, không bảo đảm quyết định của bất kỳ cơ quan nào. Nếu
        bạn không muốn tự đối chiếu yêu cầu của từng cơ quan, đội ngũ hơn 15 năm kinh nghiệm của chúng tôi có thể tư vấn
        chọn tuyến và thực hiện toàn bộ — liên hệ qua điện thoại, LINE hoặc email.
      </p>
    </article>
  );
}
